// Thrive replaces Measure in the partner rail (Paul, 2026-09-06) - every old
// /measure* URL must keep resolving, as a redirect, forever (bookmarks,
// emailed links, the old rail position). This file is the ONE place that
// mapping lives, mirroring the /bookmobile -> /outreach precedent in app.ts:
// pure functions here, a thin queueMicrotask(navigate(...)) wrapper at each
// redirect route in app.ts.
//
// The drill's registry keys (measure/registry.ts: 'circ', 'circ/physical',
// 'circ/digital', 'prog', 'spaces', 'staffing', 'comms', 'community') are
// reused verbatim as Thrive's own sub-paths - only the AREA segment is new
// (Collections / Operations / Patrons and Community). A dynamic level's id
// tail ('prog/cat/7') rides through unchanged; only the prefix decides the
// area.
const AREA_FOR_DRILL_PREFIX: Readonly<Record<string, string>> = {
	circ: 'collections',
	prog: 'operations',
	spaces: 'operations',
	staffing: 'operations',
	comms: 'operations',
	community: 'patrons',
};

/** '/thrive' - where a bare /measure lands. */
export const THRIVE_OVERVIEW_PATH = '/thrive';

/** '/thrive/reports/pipeline' - where /measure/pipeline lands (staff only). */
export const THRIVE_PIPELINE_PATH = '/thrive/reports/pipeline';

function stripSlashes(raw: string): string {
	return raw.replace(/^\/+|\/+$/g, '');
}

/** Maps a /measure/d/<tail> tail (the router's wildcard capture - no
 *  leading/trailing slashes required) to its Thrive path, area-prefixed.
 *  An empty tail, or one whose prefix isn't a known drill domain, falls
 *  back to the Overview path - the same "land somewhere useful, never a
 *  dead link" rule measure/registry.ts's own nearest-ancestor resolution
 *  follows. */
export function thriveDrillPath(tail: string): string {
	const clean = stripSlashes(tail);
	if (!clean) return THRIVE_OVERVIEW_PATH;
	const prefix = clean.split('/')[0];
	const area = AREA_FOR_DRILL_PREFIX[prefix];
	return area ? `/thrive/${area}/${clean}` : THRIVE_OVERVIEW_PATH;
}

function withSearch(path: string, search: string): string {
	if (!search) return path;
	const qs = search.startsWith('?') ? search : `?${search}`;
	return qs === '?' ? path : `${path}${qs}`;
}

/** The full redirect target for each retired /measure* route, search string
 *  (branch/period/name filters) carried through unchanged. `kind` names
 *  which of the three old route patterns matched - app.ts's three redirect
 *  stubs each pass their own kind (and, for 'drill', the wildcard tail). */
export type MeasureRedirectKind =
	| { kind: 'overview' }
	| { kind: 'pipeline' }
	| { kind: 'drill'; tail: string };

export function thriveRedirectFor(target: MeasureRedirectKind, search = ''): string {
	if (target.kind === 'overview') return withSearch(THRIVE_OVERVIEW_PATH, search);
	if (target.kind === 'pipeline') return withSearch(THRIVE_PIPELINE_PATH, search);
	return withSearch(thriveDrillPath(target.tail), search);
}
